import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import axios from "axios";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import { useNavigate } from "react-router-dom";

/**
 * QuickIdNavigator
 * A reusable compact navigation pill for moving between numeric IDs.
 *
 * Props:
 * - currentId: number | string — Current entity ID
 * - entityPath: string — API resource path (e.g., "vehicles")
 * - toPathBase?: string — Base route for navigation (default: "/<entityPath>")
 * - className?: string — Optional container class override
 * - showId?: boolean — Show the current ID in the pill (default: true)
 * - formatId?: (id: number) => string — Formatter for middle label (default: #<id>)
 * - idField?: string — Field to read ID from items (default: "id")
 * - enableKeyboard?: boolean — Enable ArrowLeft/ArrowRight keyboard navigation (default: true)
 */
function QuickIdNavigator({
  currentId,
  entityPath,
  toPathBase,
  className,
  showId = true,
  formatId,
  idField = "id",
  enableKeyboard = true,
}) {
  const navigate = useNavigate();
  const [ids, setIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const numericCurrentId = Number(currentId);
  const canUseId = Number.isFinite(numericCurrentId);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        if (!entityPath) return;
        setLoading(true);
        const url = `${config.apiUrl}/api/${entityPath}/ids`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${getCookie("_AT")}` },
        });
        const dataArray = Array.isArray(response?.data) ? response.data : [];
        const normalized = dataArray
          .map((item) => Number(item?.[idField]))
          .filter((n) => Number.isFinite(n));
        if (!ignore) setIds(normalized);
      } catch (e) {
        if (!ignore) setIds([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [entityPath, idField]);

  const sortedIds = useMemo(() => {
    return ids.slice().sort((a, b) => a - b);
  }, [ids]);

  const prevId = useMemo(() => {
    if (!canUseId || sortedIds.length === 0) return null;
    let candidate = null;
    for (let i = 0; i < sortedIds.length; i++) {
      const id = sortedIds[i];
      if (id < numericCurrentId) candidate = id;
      else break;
    }
    return candidate;
  }, [canUseId, numericCurrentId, sortedIds]);

  const nextId = useMemo(() => {
    if (!canUseId || sortedIds.length === 0) return null;
    for (let i = 0; i < sortedIds.length; i++) {
      const id = sortedIds[i];
      if (id > numericCurrentId) return id;
    }
    return null;
  }, [canUseId, numericCurrentId, sortedIds]);

  const goTo = (targetId) => {
    const base = toPathBase || `/${entityPath}`;
    navigate(`${base}/${targetId}`);
  };

  // Keyboard navigation: use ArrowLeft/ArrowRight
  useEffect(() => {
    if (!enableKeyboard) return;
    const onKeyDown = (e) => {
      // Avoid when typing in inputs/textareas or contenteditable
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        target?.isContentEditable === true;
      if (isTyping) return;

      if (e.key === "ArrowLeft" && prevId && !loading) {
        e.preventDefault();
        goTo(prevId);
      } else if (e.key === "ArrowRight" && nextId && !loading) {
        e.preventDefault();
        goTo(nextId);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enableKeyboard, prevId, nextId, loading]);

  const label = useMemo(() => {
    if (!showId) return null;
    if (!canUseId) return "";
    return typeof formatId === "function"
      ? formatId(numericCurrentId)
      : `#${numericCurrentId}`;
  }, [showId, canUseId, numericCurrentId, formatId]);

  return (
    <>
      <div
        className={
          className ||
          "inline-flex items-center rounded-full bg-white/80 backdrop-blur px-1 py-1 shadow border"
        }
      >
        <button
          type="button"
          title={prevId ? `Go to #${prevId}` : "No previous"}
          aria-label="Previous"
          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-700 hover:text-primary hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => prevId && goTo(prevId)}
          disabled={!prevId || loading}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        {showId && (
          <span className="mx-2 text-xs text-gray-600 select-none">{label}</span>
        )}
        <button
          type="button"
          title={nextId ? `Go to #${nextId}` : "No next"}
          aria-label="Next"
          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-700 hover:text-primary hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => nextId && goTo(nextId)}
          disabled={!nextId || loading}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

    </>
  );
}

export default QuickIdNavigator;


